import React, { useEffect, useRef, useState } from "react"
import { Modal, Button, Select, Flex, Input, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { CheckOutlined, CloseCircleOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useMediaQuery } from "react-responsive"
import { createCategory, getCategories } from "store/trips"
import { api } from "store/helpers/api"
import styles from "./index.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import CustomDrawer from "ui/Drawer"

// The trip's category comes back as an object ({ id, name }), a bare id, or (legacy) a name
// string. Resolve it to the catalog id the Select options are keyed by.
const resolveCategoryId = (raw, catalog) => {
  if (raw == null) return null
  if (typeof raw === "object") return raw.id ?? null
  if (typeof raw === "number") return raw
  const byName = catalog.find((category) => category.name?.toLowerCase() === String(raw).toLowerCase())
  if (byName) return byName.id
  const asId = Number(raw)
  return !Number.isNaN(asId) && catalog.some((category) => category.id === asId) ? asId : null
}

const EditCategoryModal = ({ visible, onClose, onSave, tripId, loading }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  // Backend-keyed categories ([{ id, name }]); the selected value is the numeric id sent
  // as category_ids, matching the wizard's Details step.
  const categories = useSelector((state) => state.trips.categories.data?.categories || [])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isLoadingTripCategory, setIsLoadingTripCategory] = useState(false)
  const [isCreatingInline, setIsCreatingInline] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState("")
  const [isCreatingLoading, setIsCreatingLoading] = useState(false)
  // Latest catalog, read inside the fetch callback to resolve a legacy name string without
  // adding `categories` to the fetch effect's deps (which would re-fire the request).
  const categoriesRef = useRef(categories)
  categoriesRef.current = categories

  // Fetch the category list when the modal opens (if not already loaded).
  useEffect(() => {
    if (visible && categories.length === 0) dispatch(getCategories())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // On open: clear transient UI, then fetch the trip's current category and seed the Select
  // straight from the response. Seeding inside the callback (not a derived effect) means the
  // box reflects exactly what the server returned this open — no stale carry-over from the
  // previous open, since the modal never unmounts. A manual pick afterwards updates
  // selectedCategory via onChange and is never overwritten.
  //
  // Direct api() (not getGuideTrip) so it (a) doesn't flip the shared trips.trip loading flag
  // the Save button watches, and (b) carries a `_t` cache-buster — the GET URL is otherwise
  // identical each open, so the browser would serve a stale body right after a save.
  useEffect(() => {
    if (!visible) return
    setIsCreatingInline(false)
    setNewCategoryName("")
    setSelectedCategory(null)
    if (!tripId) return

    let active = true
    setIsLoadingTripCategory(true)
    dispatch(api({ url: `/guide/trips/${tripId}`, params: { include_boats: false, _t: Date.now() } }))
      .then((result) => {
        if (!active) return
        const fullTrip = result?.data || result
        const raw = Array.isArray(fullTrip?.categories) ? fullTrip.categories[0] : fullTrip?.categories
        setSelectedCategory(resolveCategoryId(raw, categoriesRef.current))
      })
      .catch(() => {})
      .finally(() => {
        if (active) setIsLoadingTripCategory(false)
      })
    return () => {
      active = false
    }
  }, [visible, tripId, dispatch])

  const handleSave = () => {
    onSave(selectedCategory)
  }

  const handleCancel = () => {
    setSelectedCategory(null)
    setIsCreatingInline(false)
    setNewCategoryName("")
    onClose()
  }

  const handleInlineSave = async () => {
    const name = newCategoryName.trim()
    if (!name) return
    setIsCreatingLoading(true)
    try {
      const result = await dispatch(createCategory(name))
      const newId = result?.data?.category?.id
      if (newId) setSelectedCategory(newId)
      setIsCreatingInline(false)
      setNewCategoryName("")
    } catch {
      // Keep the inline input open (with its text) so the user can retry;
      // the api helper already surfaces the error notification globally.
    } finally {
      setIsCreatingLoading(false)
    }
  }

  const dropdownRender = (menu) => (
    <div>
      {menu}
      {isCreatingInline ? (
        <Flex align="center" gap={8} className={styles.inlineCreateRow}>
          <Input
            autoFocus
            placeholder="Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onPressEnter={handleInlineSave}
            className={styles.inlineCreateInput}
            disabled={isCreatingLoading}
          />
          <Button
            type="text"
            icon={<CheckOutlined />}
            onClick={handleInlineSave}
            loading={isCreatingLoading}
            disabled={!newCategoryName.trim()}
          />
          <Button
            type="text"
            icon={<CloseCircleOutlined />}
            onClick={() => { setIsCreatingInline(false); setNewCategoryName("") }}
            disabled={isCreatingLoading}
          />
        </Flex>
      ) : (
        <Flex align="center">
          <Button type="link" icon={<PlusOutlined />} onClick={() => setIsCreatingInline(true)}>
            Create Category
          </Button>
        </Flex>
      )}
    </div>
  )

  const formContent = (
    <>
      <div className={styles.formGroup}>
        <LabeledField labelKey={t("equipment.edit_category_modal.category_label")}>
          {isLoadingTripCategory ? (
            <Flex align="center" justify="center" className={styles.categoryLoading}>
              <Spin />
            </Flex>
          ) : (
            <Select
              placeholder={t("equipment.edit_category_modal.select_category_placeholder")}
              value={selectedCategory}
              onChange={setSelectedCategory}
              className={styles.select}
              size="large"
              optionLabelProp="label"
              dropdownRender={dropdownRender}
              showSearch
              filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              options={categories.map((category) => ({
                value: category.id,
                label: category.name
              }))}
            />
          )}
        </LabeledField>
      </div>

      <Flex align="center" justify="space-between" gap={10} className={styles.editCategoryModalButtons}>
        <Button size="large" color="primary" variant="outlined" onClick={handleCancel} disabled={loading}>
          {t("equipment.edit_category_modal.cancel_button")}
        </Button>
        <Button size="large" color="primary" variant="solid" onClick={handleSave} disabled={selectedCategory == null} loading={loading}>
          {t("equipment.edit_category_modal.changes_button")}
        </Button>
      </Flex>
    </>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={visible} setOpen={handleCancel} title={t("equipment.edit_category_modal.title")}>
          <div className={styles.container}>{formContent}</div>
        </CustomDrawer>
      ) : (
        <Modal
          open={visible}
          onCancel={handleCancel}
          closeIcon={<CloseOutlined className={styles.closeIcon} />}
          footer={null}
          width={400}
          className={styles.modal}
          centered
          maskClosable={false}
        >
          <div className={styles.container}>
            <h2 className={styles.title}>{t("equipment.edit_category_modal.title")}</h2>
            {formContent}
          </div>
        </Modal>
      )}
    </>
  )
}

EditCategoryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool
}

EditCategoryModal.defaultProps = {
  tripId: null,
  loading: false
}

export default EditCategoryModal
